import { useForm } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormControl } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLogInteraction } from '@/hooks/useStakeholderInteractions';
import { useAuth } from '@/hooks/useAuth';
import { useLocalStorageManager } from '@/hooks/useLocalStorageManager';
import type { CRMStakeholder, InteractionType } from '@/lib/crm/types';
import { toast } from 'sonner';

interface LogInteractionDialogProps {
  stakeholders: CRMStakeholder[];
  preselectedStakeholder?: CRMStakeholder | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  prefilledDate?: Date;
}

interface FormData {
  stakeholder_id: string;
  date: string;
  type: InteractionType;
  subject: string;
  outcome: string;
  notes: string;
  attendees: string;
  nextFollowUp: string;
  status: 'pending' | 'completed' | 'cancelled';
}

export function LogInteractionDialog({ 
  stakeholders, 
  preselectedStakeholder, 
  isOpen, 
  onClose, 
  onSuccess, 
  prefilledDate 
}: LogInteractionDialogProps) {
  const { user } = useAuth();
  const { mutateAsync: logInteraction, isPending } = useLogInteraction();
  const { getStandardValue, setStandardValue, removeStandardValue } = useLocalStorageManager();
  
  const draftKey = `log-interaction-draft-${preselectedStakeholder?.id || 'global'}`;
  const hasLoadedDraftRef = useRef(false);
  
  const form = useForm<FormData>({
    defaultValues: {
      stakeholder_id: preselectedStakeholder?.id || '',
      date: prefilledDate 
        ? format(prefilledDate, "yyyy-MM-dd'T'HH:mm")
        : format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      type: 'Meeting',
      subject: '',
      outcome: '',
      notes: '',
      attendees: '',
      nextFollowUp: '',
      status: 'completed'
    }
  });
  
  // Load draft on mount
  useEffect(() => {
    if (isOpen && !hasLoadedDraftRef.current) {
      const draft = getStandardValue<FormData>(draftKey, null as any);
      if (draft) {
        form.reset({
          stakeholder_id: preselectedStakeholder?.id || draft.stakeholder_id,
          date: prefilledDate ? format(prefilledDate, "yyyy-MM-dd'T'HH:mm") : draft.date,
          type: draft.type,
          subject: draft.subject,
          outcome: draft.outcome,
          notes: draft.notes,
          attendees: draft.attendees,
          nextFollowUp: draft.nextFollowUp,
          status: draft.status
        });
      } else if (preselectedStakeholder) {
        form.setValue('stakeholder_id', preselectedStakeholder.id);
      }
      if (prefilledDate) {
        form.setValue('date', format(prefilledDate, "yyyy-MM-dd'T'HH:mm"));
        form.setValue('nextFollowUp', format(prefilledDate, 'yyyy-MM-dd'));
      }
      hasLoadedDraftRef.current = true;
    }
    
    // Reset flag when dialog closes
    if (!isOpen) {
      hasLoadedDraftRef.current = false;
    }
  }, [isOpen, draftKey, getStandardValue, preselectedStakeholder, prefilledDate, form]);
  
  // Auto-save draft
  useEffect(() => {
    if (!isOpen) return;
    const subscription = form.watch((values) => {
      setStandardValue(draftKey, values as FormData);
    });
    return () => subscription.unsubscribe();
  }, [isOpen, form, draftKey, setStandardValue]);
  
  const onSubmit = async (data: FormData) => {
    if (!data.stakeholder_id) {
      toast.error('Vui lòng chọn stakeholder');
      return;
    }
    
    await logInteraction({
      stakeholder_id: data.stakeholder_id,
      date: new Date(data.date).toISOString(),
      type: data.type,
      subject: data.subject,
      outcome: data.outcome,
      notes: data.notes,
      attendees: data.attendees,
      next_follow_up: data.nextFollowUp || undefined,
      status: data.status,
      created_by: user?.id
    });
    
    removeStandardValue(draftKey);
    onSuccess();
    onClose();
    form.reset();
  };
  
  const handleClose = () => {
    removeStandardValue(draftKey);
    form.reset();
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Ghi nhận Tương tác</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              name="stakeholder_id"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Stakeholder *</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Chọn stakeholder..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="max-h-[300px]">
                      {stakeholders.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} {s.role && `- ${s.role}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            
            <FormField
              name="date"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ngày & Giờ *</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="type"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Loại tương tác</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Meeting">Meeting</SelectItem>
                      <SelectItem value="Call">Call</SelectItem>
                      <SelectItem value="Site Visit">Site Visit</SelectItem>
                      <SelectItem value="Delivery">Delivery</SelectItem>
                      <SelectItem value="Approval">Approval</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            
            <FormField
              name="subject"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Chủ đề</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Nội dung cuộc họp" />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="outcome"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Kết quả</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Những điều đã quyết định..." />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="notes"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ghi chú</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Chi tiết bổ sung..." />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="attendees"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Người tham dự</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Nguyễn Văn A, Trần Thị B..." />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="status"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Trạng thái</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            
            <FormField
              name="nextFollowUp"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Follow-up tiếp theo (Tùy chọn)</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
                Hủy
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Đang lưu...' : 'Lưu tương tác'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
